<template>
  <div class="file-smart-folders-container relative h-full">
    <div class="flex flex-col h-full mx-4">
      <SmartFoldersToolbar
        @breadcrumb-click="onBreadcrumbClick"
        @breadcrumb-reset="onBreadcrumbReset"
        @configure="onSmartFoldersConfigure"
      />
      <NGrid class="h-full overflow-hidden" cols="12" item-responsive>
        <NGridItem class="h-full overflow-auto" :span="isContentPanelOpen ? '12 750:7 850:8 1400:9' : '12'">
          <div class="file-smart-folders" :class="{ 'pr-3': isContentPanelOpen || selectedSection === 'all' }">
            <FileSmartFoldersConfigurationModal v-if="showConfigurationModal" @close="showConfigurationModal = false" />
            <div v-else-if="store.state.isLoading" class="flex flex-col items-center justify-center h-[200px]">
              <NSpin />
            </div>
            <FileSmartFoldersMissingConfig
              v-else-if="!store.state.hierarchyLevels?.length"
              @configure="onSmartFoldersConfigure"
            />
            <div v-else>
              <CList
                ref="listRef"
                class="rounded-2 flex bg-base"
                :data="[...displayItems]"
                :max-row-height="store.state.viewMode === 'files' ? 200 : 50"
                @item:click="onRowClick"
              >
                <template #item="{ item, isSelected, isDraggedOver, isContextMenuOpen }">
                  <SmartFolderListItem
                    :filters="computed(() => ({}) as Record<string, any>)"
                    :is-context-menu-open="isContextMenuOpen"
                    :is-dragged-over="isDraggedOver"
                    :is-selected="isSelected"
                    :item="item"
                    :name="getSmartFolderDisplayName(item)"
                    :style="{ opacity: item.opacity || 1 }"
                  >
                    <template v-if="store.state.viewMode === 'files'" #actions>
                      <CButton
                        v-tooltip="t('canvasUI.browserApp.foldersFilesTable.openFileDetailsPanel')"
                        circle
                        class="h-9 w-9"
                        :color="themeVars.primary"
                        secondary
                        type="default"
                        @click.stop="openFileDetails(getTypedItem(item))"
                      >
                        <template #icon>
                          <CIcon icon="info-circle" size="12px" />
                        </template>
                      </CButton>
                      <CButton
                        v-if="enabledFavorites"
                        v-tooltip="
                          !!item.favoriteId
                            ? t('canvasUI.browserApp.foldersFilesTable.removeFromFavorites')
                            : t('canvasUI.browserApp.foldersFilesTable.addToFavorites')
                        "
                        circle
                        class="h-9 w-9"
                        :color="!!item.favoriteId ? themeVars.accent : themeVars.primary"
                        :secondary="!item.favoriteId"
                        type="default"
                        @click.stop="toggleItemFavorite(item as ViewCompactFile)"
                      >
                        <template #icon>
                          <CIcon :fa-type="item.favoriteId ? 'fas' : undefined" icon="heart" size="12px" />
                        </template>
                      </CButton>
                      <CButton
                        v-tooltip="t('canvasUI.browserApp.contextMenu.tooltip')"
                        circle
                        class="h-9 w-9"
                        secondary
                        type="default"
                        @click="toggleCtxMenu(getTypedItem(item), isContextMenuOpen, $event)"
                      >
                        <template #icon>
                          <CIcon class="ml-[2px] color-primary" icon="ellipsis" size="12" />
                        </template>
                      </CButton>
                    </template>
                  </SmartFolderListItem>
                </template>
                <template #item:context-menu="{ item }">
                  <ContextMenuDropdown
                    :key="forceRerenderContextMenuDropdownKey"
                    :item="getTypedItem(item)"
                    @click="() => toggleCtxMenu(getTypedItem(item), true)"
                    @open-details-panel="openFileDetails"
                    @toggle-favorite="toggleItemFavorite"
                  />
                </template>
              </CList>
              <NPagination
                v-if="showPagination"
                v-model:page="store.state.page"
                class="mt-4 flex justify-center"
                :page-count="Math.ceil(store.state.totalCount / store.state.pageSize)"
                :page-size="store.state.pageSize"
                @update:page="onPageChange"
              />
            </div>
          </div>
        </NGridItem>
        <NGridItem
          v-if="isContentPanelOpen || selectedSection === 'all'"
          class="h-full overflow-auto"
          span="0 750:5 850:4 1400:3"
        >
          <Panel v-if="isContentPanelOpen" :isLoading="isPanelLoading" @close="toggleContentPanel">
            <slot v-if="!!(slots['single-file-details'] && selectedFile)" name="single-file-details" />
            <CFilePanel v-else-if="selectedFile?.id" :file="selectedFile" is-file-browser />
            <NoItemPanel v-else />
          </Panel>
        </NGridItem>
      </NGrid>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ref, computed, inject, nextTick, onMounted, onBeforeUnmount, useSlots, type ComputedRef, watch } from 'vue'
import { NGrid, NGridItem, NPagination, NSpin } from 'naive-ui'
import { useLocalStorage } from '@vueuse/core'
import type { FileRetrieve } from '@openapi'

import Panel from '@browser/components/Content/Panel/Panel.vue'
import NoItemPanel from '@browser/components/Content/Panel/NoItemPanel/NoItemPanel.vue'
import SmartFoldersToolbar from '@browser/components/FileSmartFoldersToolbar.vue'
import CFilePanel from '@lib/components/CFilePanel/CFilePanel.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CList from '@lib/components/CList/CList.vue'
import SmartFolderListItem from '@browser/components/Content/Common/SmartFolderListItem.vue'
import FileSmartFoldersConfigurationModal from '@browser/components/FileSmartFoldersConfigurationModal.vue'
import FileSmartFoldersMissingConfig from '@browser/components/FileSmartFoldersMissingConfig.vue'
import ContextMenuDropdown from '@lib/apps/browser/components/ContextMenu/ContextMenuDropdown.vue'
import useFileSmartFolderStore from '@browser/stores/fileSmartFolderStore'
import useAppStore from '@browser/stores/app'
import useThemeVars from '@lib/composables/useThemeVars'
import { openItem } from '@browser/util/file'
import { ViewCompactItemType, type ViewCompactFile, type ViewCompactFileFolder } from '@lib/types/files'
import type { BrowserAppProps } from '@browser/appTypes'

const appProps = inject<ComputedRef<BrowserAppProps | null>>(
  'appProps',
  computed(() => null),
)

const slots = useSlots()

const { t } = useI18n()
const themeVars = useThemeVars()
const store = useFileSmartFolderStore()
const { selectedSection, enabledFavorites } = useAppStore()
const listRef = ref()
const showConfigurationModal = ref(false)
const selectedFile = ref<FileRetrieve | null | undefined>(null)
const isContentPanelOpen = ref(false)
const isPanelLoading = ref(false)
const forceRerenderContextMenuDropdownKey = ref(0)
const displayItems = computed(() => {
  if (!store.state.hierarchyLevels?.length) return []

  if (store.state.viewMode === 'files') {
    return (
      store.state.matchingFiles?.map((item) => ({
        ...item,
        favoriteId: item.favorite_id,
        type: 'file',
      })) ?? []
    )
  }

  return (
    store.state.availableValues?.map((item) => ({
      ...item,
      id: item.value,
    })) ?? []
  )
})

const myUser = inject<ComputedRef<{ id: string }>>(
  'myUser',
  computed(() => ({ id: '' })),
)

const pitcherInfo = inject<ComputedRef<{ instanceId: string }>>(
  'pitcherInfo',
  computed(() => ({ instanceId: '' })),
)

const storedPath = useLocalStorage<Array<string>>(
  `file_smart-folders_${pitcherInfo.value.instanceId}_${myUser.value.id}_current_path`,
  [],
  {
    serializer: {
      read: (v: string) => {
        try {
          return JSON.parse(v)
        } catch {
          return []
        }
      },
      write: (v: Array<string>) => JSON.stringify(v),
    },
  },
)

const getTypedItem = (item: any) => item as ViewCompactFile

const showPagination = computed(() => store.state.viewMode === 'files' && store.state.totalCount > store.state.pageSize)
const hideFileDetails = computed(() => store.state.viewMode !== 'files')

async function openFileDetails(item: ViewCompactFileFolder) {
  isContentPanelOpen.value = true
  isPanelLoading.value = true
  selectedFile.value = await appProps.value?.onFileFetch?.(item.id)
  isPanelLoading.value = false
}

function toggleItemFavorite(item: ViewCompactFileFolder & { favoriteId?: string }): void {
  if (!item.favoriteId) {
    appProps.value?.onFavoriteCreate?.({ file_id: item.id }).then((favorite) => {
      store.actions.setMatchingFiles(
        store.state.matchingFiles?.map((file) =>
          file.id === favorite.file?.id ? { ...file, favorite_id: favorite.id } : file,
        ),
      )
    })
  } else {
    appProps.value
      ?.onFavoriteDelete?.(item.favoriteId)
      .then(() => {
        store.actions.setMatchingFiles(
          store.state.matchingFiles?.map((file) =>
            file.favorite_id === item?.favoriteId ? { ...file, favorite_id: undefined } : file,
          ),
        )
      })
      .catch((e) => {
        e.status === 404 &&
          store.actions.setMatchingFiles(
            store.state.matchingFiles?.map((file) => ({ ...file, favorite_id: undefined })),
          )
      })
  }
}

function toggleContentPanel() {
  isContentPanelOpen.value = !isContentPanelOpen.value
}

function onSmartFoldersConfigure() {
  showConfigurationModal.value = true
}

function onBreadcrumbClick(index: number) {
  store.actions.navigateToLevel(index + 1)

  const pathValues = store.state.currentPath?.map((p) => p.value) ?? []
  storedPath.value = [...pathValues]
}

function onBreadcrumbReset() {
  store.actions.navigateToLevel(0)
  storedPath.value = []
}

function onRowClick(item: FileRetrieve | { value: string; count: number; type?: ViewCompactItemType }, e: MouseEvent) {
  if (item?.type === ViewCompactItemType.SmartFolder) {
    store.actions.selectValue(item.value)
    const pathValues = store.state.currentPath?.map((p) => p.value) ?? []
    storedPath.value = [...pathValues]
  } else {
    openItem(item as FileRetrieve, e)
  }
}

function onPageChange() {
  store.actions.fetchData()
}

function getSmartFolderDisplayName(item: any) {
  if (!store.state.hierarchyLevels?.length) return item.value
  const displayNameDict = store.state.hierarchyLevels?.[store.state.currentPath?.length]?.display_names_by_value
  return displayNameDict?.[item.value] ?? (Array.isArray(item.value) ? item.value.join(', ') : item.value)
}

function toggleCtxMenu(item: ViewCompactFileFolder, isContextMenuOpen: boolean, e?: MouseEvent) {
  const listInstance = listRef.value as InstanceType<typeof CList> | null
  listInstance?.updateContextMenuItem(item, !isContextMenuOpen, e)
  nextTick(() => {
    forceRerenderContextMenuDropdownKey.value++
  })
}

onMounted(async () => {
  await store.actions.loadConfiguration()
  if (store.state.hierarchyLevels && store.state.hierarchyLevels.length > 0) {
    if (storedPath.value.length > 0) {
      storedPath.value.forEach((pathValue, index) => {
        store.actions.selectValue(pathValue, { skipFetchData: index < storedPath.value.length - 1 })
      })
    } else {
      store.actions.navigateToLevel(0)
    }
  }
})

onBeforeUnmount(() => {
  store.actions.navigateToLevel(0)
})

watch(hideFileDetails, (shouldHide) => {
  if (shouldHide) {
    isContentPanelOpen.value = false
  }
})

defineExpose({
  showConfigurationModal: onSmartFoldersConfigure,
})
</script>
